<!--
  =====================================================================
  APP ROUTER - DYNAMIC PAGE LOADER
  =====================================================================
  
  Root component that dynamically routes to different page views based on
  the current URL pathname. Handles both direct routes (e.g., /fondazione/news)
  and dynamic routes with parameters (e.g., /fondazione/news/news-slug).
  
  URL ROUTING PATTERNS:
  - /fondazione/                         → HomePage
  - /fondazione/chi-siamo                → ChiSiamoPage
  - /fondazione/attivita                 → ActivityHome
  - /fondazione/attivita/:macroArea/:category        → ActivityCategory
  - /fondazione/attivita/:macroArea/:category/:product → ActivityDetail
  - /fondazione/opere-arte               → OperePage
  - /fondazione/news                     → NewsPage
  - /fondazione/news/:slug               → NewsSinglePage
  - /fondazione/informative-al-pubblico  → InformativeAlPubblicoPage
  - /fondazione/informative-al-pubblico/:slug → InformativeAlPubblicoSinglePage
  
  NOTES:
  - Supports legacy URLs without /fondazione prefix (redirects via handler)
  - Uses 'popstate' event listener to handle browser back/forward buttons
  - Routing logic is organized by specificity (detailed routes first)
  
  =====================================================================
-->

<template>
  <div>
    <component :is="componenteCorrente" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

// ───────────────────────────────────────────────────────────────────
// PAGE VIEW IMPORTS
// ───────────────────────────────────────────────────────────────────
import HomePage from './views/HomePage.vue'
import ChiSiamoPage from './views/ChiSiamoPage.vue'
import ActivityHome from './views/ActivityHome.vue'
import ActivityCategory from './views/ActivityCategory.vue'
import ActivityDetail from './views/ActivityDetail.vue'
import OperePage from './views/OperePage.vue'
import NewsPage from './views/NewsPage.vue'
import NewsSinglePage from './views/NewsSinglePage.vue'
import InformativePage from "@/views/InformativeAlPubblicoPage.vue"
import InformativaSinglePage from "@/views/InformativeAlPubblicoSinglePage.vue"
import NotFoundPage from './views/NotFoundPage.vue'

// Import global state for 404 handling
import { state } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE
// ───────────────────────────────────────────────────────────────────

/** Current pathname tracked for routing decisions */
const percorso = ref(window.location.pathname)

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize router event listeners
 * - Listens for popstate events (browser back/forward buttons)
 * - Updates current route when user navigates via browser buttons
 * - Shows loading screen on route change
 * - Redirects URLs without /fondazione/ prefix to home
 */
watch(() => state.loading, (isLoading) => {
  const loadingScreen = document.getElementById('loading-screen')
  if (!loadingScreen) return
  if (isLoading) {
    loadingScreen.classList.remove('hidden')
  } else {
    loadingScreen.classList.add('hidden')
  }
})

onMounted(() => {
  // ✅ Redirect URLs without /fondazione/ prefix to home
  const currentPath = window.location.pathname
  if (!currentPath.startsWith('/fondazione/') && currentPath !== '/fondazione') {
    console.info(`Redirect: ${currentPath} → /fondazione/`)
    window.location.href = '/fondazione/'
    return // Stop execution
  }

  window.addEventListener('popstate', () => {
    percorso.value = window.location.pathname
  })
})

// ───────────────────────────────────────────────────────────────────
// STATIC ROUTE MAPPING
// ───────────────────────────────────────────────────────────────────

/**
 * Maps simple routes (without parameters) to their corresponding components
 * These are fallback routes when no dynamic path matches
 */
const mappa = {
  '/fondazione/': HomePage,
  '/fondazione/chi-siamo': ChiSiamoPage,
  '/fondazione/attivita': ActivityHome,
  '/fondazione/opere-arte': OperePage,
  '/fondazione/news': NewsPage,
  '/fondazione/informative-al-pubblico': InformativePage,
}

// ───────────────────────────────────────────────────────────────────
// DYNAMIC ROUTE RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Resolves the current page component based on the URL pathname
 * 
 * Routing algorithm:
 * 1. Parse URL path into segments, removing empty parts
 * 2. Skip 'fondazione' prefix if present (Nginx serves app under /fondazione/)
 * 3. Check for detailed routes first (longer path segments)
 * 4. Then check for main section routes
 * 5. Finally fall back to static mapping
 * 
 * @returns {VueComponent} The component to render for current route
 */
const componenteCorrente = computed(() => {
  // ✅ PRIORITY 1: Check for centralized 404 handling
  if (state.notFound) {
    return NotFoundPage
  }
  
  const pathSegments = percorso.value.split('/').filter(Boolean)
  
  // Normalize path: remove 'fondazione' prefix if present
  const segments = pathSegments[0] === 'fondazione' ? pathSegments.slice(1) : pathSegments
  
  // ─── ATTIVITÀ: Detailed Routes (3-4 segments) ───────────────────
  // Activity Detail: /fondazione/attivita/:macroArea/:category/:product
  if (segments[0] === 'attivita' && segments.length === 4) {
    return ActivityDetail
  }
  
  // Activity Category: /fondazione/attivita/:macroArea/:category
  if (segments[0] === 'attivita' && segments.length === 3) {
    return ActivityCategory
  }

  // Activity Home: Various formats
  if (percorso.value === '/attivita' || percorso.value === '/attivita/' || 
      percorso.value === '/fondazione/attivita' || percorso.value === '/fondazione/attivita/')
    return ActivityHome

  // ─── NEWS: Detailed Routes ────────────────────────────────────
  // News Single: /fondazione/news/:slug
  if ((percorso.value.startsWith('/news/') || percorso.value.startsWith('/fondazione/news/')) && 
      percorso.value !== '/news' && percorso.value !== '/fondazione/news')
    return NewsSinglePage

  // ─── INFORMATIVE: Detailed Routes ─────────────────────────────
  // Informativa Single: /fondazione/informative-al-pubblico/:slug
  if ((percorso.value.startsWith('/informative-al-pubblico/') || percorso.value.startsWith('/fondazione/informative-al-pubblico/')) && 
      percorso.value !== '/informative-al-pubblico' && percorso.value !== '/fondazione/informative-al-pubblico')
    return InformativaSinglePage

  // ─── FALLBACK: Static Route Mapping or 404 ──────────────────
  // Check if path is in known static routes
  if (mappa[percorso.value]) {
    return mappa[percorso.value]
  }
  
  // ✅ SOLID LOGIC: If not in mappa and not matching any dynamic patterns → 404
  // This ensures ANY unrecognized URL shows 404, not home
  return NotFoundPage
})
</script>

